import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import ShopifyBulkImportDialog from "@/components/ShopifyBulkImportDialog";
import { AiEmailCard } from "@/components/ai-email/AiEmailCard";
import { ChatGptConnectorCard } from "@/components/ai-email/ChatGptConnectorCard";
import { ReadonlyCredentialCard, FilingCredentialCard } from "@/components/ai-email/ReadonlyCredentialCard";
import { SdcEmailReadsCard } from "@/components/ai-email/SdcEmailReadsCard";
import { TrackingCard } from "@/components/TrackingCard";
import { ExternalApiCard } from "@/components/ExternalApiCard";
import { OrdersBackupCard } from "@/components/OrdersBackupCard";
import { ReviewRequestsCard } from "@/components/ReviewRequestsCard";
import { JourneyEmailsCard, JourneyFields, JourneyAutoSend, IS_JOURNEY_TPL } from "@/components/JourneyEmailsCard";
import { DriveDuplicatesCard } from "@/components/DriveDuplicatesCard";
import { ShopifyAccessCard } from "@/components/ShopifyAccessCard";
import { NaCancelSyncCard } from "@/components/NaCancelSyncCard";
import { SettingsFold, useSettingsFolds } from "@/components/SettingsFold";
import { useT } from "@/i18n/LanguageContext";

const HASH_TO_FOLD = { "#tracking": "tracking", "#ai-email": "ai-email", "#chatgpt": "chatgpt" };
const ALL_FOLDS = ["drive", "drive-duplicates", "orders-backup", "review-requests", "shopify-webhook", "shopify-access", "na-cancel-sync", "photo-reminder", "chatgpt", "readonly-credential", "filing-credential", "external-api", "tracking", "ai-email", "packing-template", "password-vault", "maintenance", "danger-zone"];

export default function Settings() {
  const { user } = useAuth();
  const { t } = useT();
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const isSuperAdmin = user.role === "super_admin";

  // Sections start folded; a deep link (#tracking …) or an OAuth return (?drive= / ?gmail=connected) unfolds the one concerned.
  const folds = useSettingsFolds((() => {
    const p = new URLSearchParams(location.search);
    return [HASH_TO_FOLD[location.hash], p.get("drive") === "connected" && "drive", p.get("gmail") === "connected" && "photo-reminder"];
  })());
  React.useEffect(() => {
    const id = HASH_TO_FOLD[location.hash];
    if (id) setTimeout(() => document.getElementById(`fold-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, []);

  // Show toast if we just came back from Google's OAuth callback
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("drive") === "connected") {
      toast.success("Google Drive connected ✓");
      qc.invalidateQueries({ queryKey: ["drive-status"] });
      navigate("/settings", { replace: true });
    }
    if (params.get("gmail") === "connected") {
      toast.success("Gmail connected ✓");
      qc.invalidateQueries({ queryKey: ["photo-reminder-status"] });
      navigate("/settings", { replace: true });
    }
  }, [location.search]);

  const [connectingSince, setConnectingSince] = React.useState(null);

  const { data: status, isLoading } = useQuery({
    queryKey: ["drive-status"],
    queryFn: async () => (await api.get("/drive/status")).data,
    // Poll every 3s for 3 min after Connect was clicked, otherwise every 30s.
    refetchInterval: () => {
      if (connectingSince && (Date.now() - connectingSince) < 180000) return 3000;
      return 30000;
    },
  });

  // Stop the fast poll once we detect connection
  React.useEffect(() => {
    if (status?.connected && connectingSince) setConnectingSince(null);
  }, [status?.connected, connectingSince]);

  const connect = useMutation({
    mutationFn: async () => (await api.get("/drive/connect")).data,
    onSuccess: (d) => {
      const url = d?.authorization_url;
      if (!url) return;
      // Emergent preview runs inside an iframe; Google's OAuth page sends
      // X-Frame-Options: SAMEORIGIN which the browser renders as "refused to
      // connect". We open in a new top-level tab so Google can render properly.
      // After the user grants access, Google redirects to /api/drive/callback,
      // which redirects to /settings?drive=connected in that same tab.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.error("Popup blocked — allow popups for this site, then click Connect again.");
        return;
      }
      setConnectingSince(Date.now());
      toast.success("Complete authorization in the new tab · this page will refresh automatically once connected.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to start OAuth"),
  });

  const disconnect = useMutation({
    mutationFn: async () => (await api.post("/drive/disconnect")).data,
    onSuccess: () => {
      toast.success("Google Drive disconnected");
      qc.invalidateQueries({ queryKey: ["drive-status"] });
    },
  });

  const syncNow = useMutation({
    mutationFn: async () => (await api.post("/drive/sync")).data,
    onSuccess: (s) => {
      toast.success(`Sync complete · ${s?.newly_linked ?? 0} newly linked · ${s?.scanned_po_folders ?? 0} folders scanned`);
      qc.invalidateQueries({ queryKey: ["drive-status"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });

  return (
    <div className="max-w-3xl space-y-3" data-testid="settings-page">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
        <div>
          <h1 className="text-2xl font-bold font-display" data-testid="settings-title">{t("Settings")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("Integrations and system connections")} · {t("press a title to unfold it")}</p>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => folds.setAll(ALL_FOLDS)} data-testid="settings-expand-all">{t("Expand all")}</Button>
          <span className="text-slate-300">|</span>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => folds.setAll([])} disabled={!folds.count} data-testid="settings-collapse-all">{t("Collapse all")}</Button>
        </div>
      </div>

      {/* Preview-mode banner — only rendered when the backend reports it's
          running with SUPPRESS_CUSTOMER_EMAILS=true. Warns the user that
          nothing they do here reaches real customers, plus exposes the
          🧹 wipe-changes button that reverts every preview-injected side
          effect (red paint on F1/F2, No Mold rows, cancelled orders). */}
      <PreviewModeBanner status={status} />

      {/* Google Drive card */}
      <SettingsFold id="drive" icon="📁" title={t("Google Drive")} folds={folds}>
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="drive-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2">
              <span className="text-2xl">📁</span>
              <h2 className="font-semibold text-lg">{t("Google Drive")}</h2>
              {isLoading ? null : status?.connected ? (
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="drive-connected-badge">
                  Connected
                </Badge>
              ) : (
                <Badge className="bg-red-50 text-red-700 border border-red-200" data-testid="drive-disconnected-badge">
                  Not connected
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Auto-links customer photos in the <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">photos of orders</code> Drive folder
              to their matching Pre Orders in the CRM. Runs every 5 minutes automatically.
            </p>
            {status?.connected && (
              <div className="mt-3 text-xs text-slate-500 space-y-0.5 font-mono">
                <div>Account: <span className="text-slate-700">{status.email || "—"}</span></div>
                <div>
                  Upload permission:{" "}
                  {status.can_upload ? (
                    <span className="text-emerald-700 font-semibold">✓ Add-only (no delete)</span>
                  ) : (
                    <span className="text-red-700 font-semibold">✕ Read-only — Re-authorize required</span>
                  )}
                </div>
                <div>
                  Sheets edit permission:{" "}
                  {status.can_edit_sheets ? (
                    <span className="text-emerald-700 font-semibold">✓ Row paint + code overwrite enabled</span>
                  ) : (
                    <span className="text-red-700 font-semibold">✕ Missing — Re-authorize required</span>
                  )}
                </div>
                <div>Connected: {status.connected_at?.slice(0, 16).replace("T", " ") || "—"}</div>
                {status.last_sync_at && (
                  <div>Last sync: {status.last_sync_at.slice(0, 16).replace("T", " ")}
                    {status.last_sync_stats && (
                      <span className="ml-2 text-slate-600">
                        · scanned {status.last_sync_stats.scanned_po_folders || 0} · newly linked {status.last_sync_stats.newly_linked || 0}
                      </span>
                    )}
                  </div>
                )}
                {status.last_sync_stats?.unmatched?.length > 0 && (
                  <details className="mt-2 text-slate-500">
                    <summary className="cursor-pointer">Unmatched folder names ({status.last_sync_stats.unmatched.length})</summary>
                    <ul className="pl-4 mt-1 space-y-0.5">
                      {status.last_sync_stats.unmatched.map((n, i) => <li key={i} className="text-[11px]">{n}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {!status?.connected && isSuperAdmin && (
              <Button
                onClick={() => connect.mutate()}
                disabled={connect.isPending}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="drive-connect-btn"
              >
                {connect.isPending ? "Redirecting…" : "Connect Google Drive"}
              </Button>
            )}
            {!status?.connected && !isSuperAdmin && (
              <p className="text-xs text-slate-500 italic max-w-[200px] text-right">
                Only Super Admin can connect Drive.
              </p>
            )}
            {status?.connected && (
              <>
                <Button
                  variant="outline"
                  onClick={() => syncNow.mutate()}
                  disabled={syncNow.isPending}
                  data-testid="drive-sync-now"
                >
                  🔄 {syncNow.isPending ? "Syncing…" : "Sync now"}
                </Button>
                {isSuperAdmin && (
                  <Button
                    onClick={() => connect.mutate()}
                    disabled={connect.isPending}
                    className={(status.can_upload && status.can_edit_sheets) ? "bg-slate-900 hover:bg-slate-800" : "bg-red-600 hover:bg-red-700 text-white"}
                    data-testid="drive-reauthorize-btn"
                    title="Re-run the Google consent screen. Grants full-Drive write permission (needed to upload packing photos into existing PO folders) AND Sheets edit permission (needed to paint rows + overwrite clamp/cover codes on the master F1/F2 sheets)."
                  >
                    {connect.isPending
                      ? "Redirecting…"
                      : (status.can_upload && status.can_edit_sheets)
                      ? "🔐 Re-authorize"
                      : "🔐 Grant Missing Access"}
                  </Button>
                )}
                {isSuperAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (confirm("Disconnect Google Drive? Auto-sync will stop until reconnected.")) {
                        disconnect.mutate();
                      }
                    }}
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    data-testid="drive-disconnect"
                  >
                    Disconnect
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </SettingsFold>

      {/* 🗂️ Duplicate order-number folders in Order Photos → photos copied into the linked folder */}
      <SettingsFold id="drive-duplicates" icon="🗂️" title={t("Duplicate order folders")} folds={folds}><DriveDuplicatesCard status={status} /></SettingsFold>

      {/* Daily orders backup → Google Sheet in BACKUP LPC (5pm Toronto) — BO / SA */}
      <SettingsFold id="orders-backup" icon="📊" title={t("Daily orders backup (Google Sheet)")} folds={folds}><OrdersBackupCard /></SettingsFold>

      {/* ⭐ Photo / review requests — 3 weeks after fulfilment + reminders, review destination switch, Installers form */}
      <SettingsFold id="review-requests" icon="⭐" title={t("Photo / review requests")} folds={folds}><ReviewRequestsCard /></SettingsFold>

      {/* ✉️ Customer journey e-mails — milestone e-mails tied to the order process (10:00 Toronto) + QC send-back alert */}
      <SettingsFold id="journey-emails" icon="✉️" title={t("Customer journey e-mails")} folds={folds}><JourneyEmailsCard /></SettingsFold>

      {/* Shopify webhook card (info-only) */}
      <SettingsFold id="shopify-webhook" icon="🛍️" title={t("Shopify Webhook")} folds={folds}>
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="shopify-card">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🛍️</span>
          <h2 className="font-semibold text-lg">{t("Shopify Webhook")}</h2>
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Active</Badge>
        </div>
        <p className="text-sm text-slate-600 mt-2">
          Incoming order webhooks auto-create Pre Orders with the note <b>"Photos not received"</b>. HMAC-SHA256 signature verified against your webhook secret.
        </p>
        <div className="mt-3 text-xs text-slate-500 font-mono">
          Webhook URL: <span className="text-slate-800">{typeof window !== "undefined" ? window.location.origin : ""}/api/shopify/webhook</span>
        </div>
      </div>
      </SettingsFold>

      {/* Shopify Admin API — full order history (read_all_orders) or last 60 days only */}
      <SettingsFold id="shopify-access" icon="🛒" title={t("Shopify Admin API access")} folds={folds}><ShopifyAccessCard /></SettingsFold>

      {/* ✕ NA orders cancelled in Shopify → Cancelled Orders (hourly + Sync now) */}
      <SettingsFold id="na-cancel-sync" icon="✕" title={t("NA orders cancelled in Shopify")} folds={folds}><NaCancelSyncCard /></SettingsFold>

      {/* Photo Reminder Emails — Gmail SMTP wiring + test send */}
      <SettingsFold id="photo-reminder" icon="📧" title={t("Photo Reminder Emails")} folds={folds}><PhotoReminderCard /></SettingsFold>

      {/* ChatGPT Connector — the user's own ChatGPT looks orders up in the CRM (remote MCP) */}
      <SettingsFold id="chatgpt" icon="🔌" title={t("ChatGPT Connector")} folds={folds}><ChatGptConnectorCard /></SettingsFold>

      {/* Read-only MCP credential for the Leprecon Service Desk (Super Admin creates / revokes) */}
      <SettingsFold id="readonly-credential" icon="🔑" title={<>{t("Read-only credential")} <span className="text-slate-400 font-normal text-sm">· {t("Service Desk")}</span></>} folds={folds}><ReadonlyCredentialCard /></SettingsFold>
      <SettingsFold id="filing-credential" icon="📨" title={<>{t("E-mail filing credential (write)")} <span className="text-slate-400 font-normal text-sm">· {t("Service Desk → Emails tab")}</span></>} folds={folds}><FilingCredentialCard /></SettingsFold>
      {/* SDC e-mail reading gate (preview_status_email / get_order_emails) — Super Admin only, off by default */}
      {isSuperAdmin && <SettingsFold id="sdc-email-reads" icon="📖" title={<>{t("SDC e-mail reading")} <span className="text-slate-400 font-normal text-sm">· {t("Service Desk")}</span></>} folds={folds}><SdcEmailReadsCard /></SettingsFold>}

      {/* External REST API keys for remote AI agents (Jasmine Ai …) — Super Admin only */}
      {isSuperAdmin && <SettingsFold id="external-api" icon="🤖" title={t("External API — AI agents")} folds={folds}><ExternalApiCard /></SettingsFold>}

      {/* Parcel tracking (17TRACK) — feeds the Inv Packing dashboard */}
      <SettingsFold id="tracking" icon="📬" title={t("Parcel tracking (17TRACK)")} folds={folds}><TrackingCard /></SettingsFold>

      {/* Optional CRM-side Gmail assistant */}
      <SettingsFold id="ai-email" icon="🤖" title={t("AI E-mail Assistant (ChatGPT)")} folds={folds}><AiEmailCard expanded /></SettingsFold>

      {/* Packing Checklist Template — Super Admin only */}
      {isSuperAdmin && <SettingsFold id="packing-template" icon="📋" title={t("Packing Checklist Template")} folds={folds}><PackingTemplateCard /></SettingsFold>}

      {/* User Password Vault — Super Admin only */}
      {isSuperAdmin && <SettingsFold id="password-vault" icon="🔐" title={t("User Password Vault")} folds={folds}><PasswordVaultCard /></SettingsFold>}

      {/* Maintenance — safe utilities for BO / SA */}
      {(user?.role === "back_office" || isSuperAdmin) && <SettingsFold id="maintenance" icon="🧰" title={t("Maintenance")} folds={folds}><MaintenanceCard /></SettingsFold>}

      {/* Danger Zone — Super Admin only */}
      {isSuperAdmin && <SettingsFold id="danger-zone" icon="🚨" title={t("Danger Zone")} tone="danger" folds={folds}><DangerZone /></SettingsFold>}
    </div>
  );
}

// ============================================================================
// Maintenance — safe backfills / re-runs that BO / SA might want to trigger
// manually after a rule change. Everything here is idempotent — pressing the
// button a second time is a no-op if there's nothing left to do.
// ============================================================================
function PreviewModeBanner({ status }) {
  const { t } = useT();
  const wipe = useMutation({
    mutationFn: async () => (await api.post("/admin/preview-cleanup")).data,
    onSuccess: (d) => {
      toast.success(
        `Preview cleanup complete · ${d.unpainted_rows} F1/F2 rows un-painted · ${d.no_mold_rows_removed} No Mold rows removed · ${d.orders_reverted} cancelled order(s) reverted`,
      );
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Preview cleanup failed"),
  });
  if (!status?.preview_mode) return null;
  return (
    <section
      className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4"
      data-testid="preview-mode-banner"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2">
            <span className="text-xl">🧪</span>
            <h2 className="font-semibold text-amber-900">{t("Preview mode — customer emails suppressed")}</h2>
          </div>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            You're on the preview environment. Every "cancel email" or auto-email fired from here is
            written into the Email Log with a <code className="font-mono">suppressed:true</code> flag but
            NEVER actually reaches a customer inbox. Real deliveries only happen on{" "}
            <code className="font-mono">crm.leprecon.plus</code>.
          </p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            Master-sheet writes (red paint on F1/F2, No Mold appends, code overwrites) DO still happen
            against your real Google Sheets — use the button below to wipe every side effect your
            preview testing produced.
          </p>
        </div>
        <Button
          onClick={() => {
            if (window.confirm("Wipe every preview-injected change from the master sheets and revert all cancelled orders back to their previous status? Real production data is untouched.")) {
              wipe.mutate();
            }
          }}
          disabled={wipe.isPending}
          data-testid="preview-cleanup-btn"
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          {wipe.isPending ? "Wiping…" : "🧹 Wipe preview test changes"}
        </Button>
      </div>
    </section>
  );
}

function MaintenanceCard() {
  const { t } = useT();
  const backfill = useMutation({
    mutationFn: async () => (await api.post("/admin/backfill-pending-to-advance")).data,
    onSuccess: (d) => {
      if (d.promoted > 0) {
        const list = (d.orders || []).slice(0, 5).map((o) => o.po).join(", ");
        const more = (d.orders || []).length > 5 ? `, +${d.orders.length - 5} more` : "";
        toast.success(`Backfill complete · ${d.promoted}/${d.checked} order(s) promoted to Advance (${list}${more})`);
      } else {
        toast(`Backfill complete · nothing to promote (${d.checked} pending/flagged order(s) checked)`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Backfill failed"),
  });

  const vinBackfill = useMutation({
    mutationFn: async () => (await api.post("/admin/backfill-vin-normalize")).data,
    onSuccess: (d) => {
      if (d.fixed > 0) {
        const list = (d.orders || []).slice(0, 5).map((o) => `${o.po}: ${o.from} → ${o.to}`).join("; ");
        const more = (d.orders || []).length > 5 ? `, +${d.orders.length - 5} more` : "";
        toast.success(`VIN backfill · ${d.fixed}/${d.checked} VIN(s) normalised (${list}${more})`);
      } else {
        toast(`VIN backfill · all ${d.checked} VIN(s) already clean, nothing to fix`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "VIN backfill failed"),
  });

  const sheetPaint = useMutation({
    mutationFn: async () => (await api.post("/admin/backfill-sheet-paint")).data,
    onSuccess: (d) => {
      if (d.triggered > 0) {
        const ok = d.painted ?? d.triggered;
        const failed = d.failed ?? 0;
        const failedOrders = (d.orders || []).filter((o) => o.status && o.status !== "ok").slice(0, 5);
        const okList = (d.orders || []).filter((o) => !o.status || o.status === "ok").slice(0, 3).map((o) => `${o.po}·${o.stage}·${o.factory}`).join("; ");
        if (failed === 0) {
          toast.success(`Sheet paint · ${ok} order(s) painted successfully (${okList}${d.triggered > 3 ? `, +${d.triggered - 3} more` : ""}). Refresh the F1 / F2 sheets to see them.`);
        } else {
          const failList = failedOrders.map((o) => `${o.po}: ${o.status}${o.error ? " · " + o.error : ""}`).join(" | ");
          toast.error(`Sheet paint · ${ok} painted · ${failed} FAILED — ${failList}`);
        }
      } else {
        toast(`Sheet paint · no Production / Inventory Packing orders to paint`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sheet paint failed"),
  });

  const removeCancelledTab = useMutation({
    mutationFn: async () => (await api.post("/admin/remove-cancelled-order-tab")).data,
    onSuccess: (d) => {
      const rows = d.masters || [];
      const summary = rows
        .map((r) => `${r.master}: ${r.status}${r.detail ? " · " + r.detail : ""}`)
        .join(" | ");
      if ((d.deleted ?? 0) > 0) {
        toast.success(`Cancelled Order tab · ${d.deleted} tab(s) deleted — ${summary}`);
      } else {
        toast(`Cancelled Order tab · nothing to delete — ${summary}`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Remove Cancelled Order tab failed"),
  });

  const purgeNaRows = useMutation({
    mutationFn: async () => (await api.post("/admin/purge-na-rows-from-no-mold")).data,
    onSuccess: (d) => {
      const removed = d.removed ?? 0;
      const pos = (d.purged_pos || []).slice(0, 8).join(", ");
      const more = (d.purged_pos || []).length > 8 ? `, +${d.purged_pos.length - 8} more` : "";
      if (removed > 0) {
        toast.success(`No Mold purge · ${removed} legacy NA row(s) removed (${pos}${more})`);
      } else {
        toast(`No Mold purge · nothing to remove${d.reason ? " (" + d.reason + ")" : ""}`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Purge NA rows failed"),
  });

  const paintUnpainted = useMutation({
    mutationFn: async () =>
      (await api.post("/admin/backfill-sheet-paint", null, { params: { only_unpainted: true } })).data,
    onSuccess: (d) => {
      if (d.triggered > 0) {
        const ok = d.painted ?? d.triggered;
        const failed = d.failed ?? 0;
        const failedOrders = (d.orders || []).filter((o) => o.status && o.status !== "ok").slice(0, 5);
        const okList = (d.orders || []).filter((o) => !o.status || o.status === "ok").slice(0, 3).map((o) => `${o.po}·${o.stage}·${o.factory}`).join("; ");
        if (failed === 0) {
          toast.success(`Unpainted repaint · ${ok} PO(s) painted (${okList}${d.triggered > 3 ? `, +${d.triggered - 3} more` : ""})`);
        } else {
          const failList = failedOrders.map((o) => `${o.po}: ${o.status}${o.error ? " · " + o.error : ""}`).join(" | ");
          toast.error(`Unpainted repaint · ${ok} painted · ${failed} FAILED — ${failList}`);
        }
      } else {
        toast(`Unpainted repaint · every Production / Inv-Packing PO is already painted`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Unpainted repaint failed"),
  });

  return (
    <section className="rounded-md border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-lg text-slate-800">{t("Maintenance")}</h2>
          <p className="text-xs text-slate-500 mt-0.5">Safe one-click backfills for rule changes that arrived after data existed.</p>
        </div>
      </div>
      <div className="mt-4 border border-slate-200 rounded-md p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-medium text-sm text-slate-800">Promote already-coded Pending / Flagged → Advance</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Scans every Pending and Flagged order and moves the ones that already have both
              cover codes (front + rear) for their factory into <b>Advance Orders</b>. Idempotent —
              press again anytime.
            </p>
          </div>
          <Button
            onClick={() => backfill.mutate()}
            disabled={backfill.isPending}
            data-testid="run-backfill-pending-advance"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {backfill.isPending ? "Running…" : "▶ Run Backfill"}
          </Button>
        </div>
      </div>
      <div className="mt-3 border border-slate-200 rounded-md p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-medium text-sm text-slate-800">Normalise VINs · O → 0 · I → 1 · Q → 0</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Scans every order that has a VIN and replaces the illegal look-alike letters
              (letters <span className="font-mono">O</span>, <span className="font-mono">I</span>, <span className="font-mono">Q</span> are NEVER valid in a VIN per ISO 3779) with their correct digits.
              Idempotent — press again anytime.
            </p>
          </div>
          <Button
            onClick={() => vinBackfill.mutate()}
            disabled={vinBackfill.isPending}
            data-testid="run-backfill-vin-normalize"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {vinBackfill.isPending ? "Running…" : "▶ Normalise VINs"}
          </Button>
        </div>
      </div>
      <div className="mt-3 border border-slate-200 rounded-md p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-medium text-sm text-slate-800">Repaint sheets for current Production / Inv Packing orders</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Fires the sheet paint + code-overwrite for EVERY order currently in <b>Production</b> (emerald)
              or <b>Inventory Packing</b> (orange). Fixes rows that were transitioned before the paint hook
              landed on preview / live. Idempotent — safe to press repeatedly. Runs in the background;
              check the F1 / F2 master sheets ~10 seconds after clicking.
            </p>
          </div>
          <Button
            onClick={() => sheetPaint.mutate()}
            disabled={sheetPaint.isPending}
            data-testid="run-backfill-sheet-paint"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {sheetPaint.isPending ? "Firing…" : "▶ Repaint Sheets"}
          </Button>
        </div>
      </div>
      <div className="mt-3 border border-slate-200 rounded-md p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-medium text-sm text-slate-800">Repaint only unpainted Production / Inv-Packing POs</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Fast path that skips orders already painted — only re-fires the sheet paint for POs whose
              <span className="font-mono"> sheet_painted_at</span> is missing OR whose last attempt ended with a
              <span className="font-mono"> sheet_paint_error</span>. Use this when a batch silently 429'd on
              their initial transition. Idempotent and quota-friendly (sequential + backoff).
            </p>
          </div>
          <Button
            onClick={() => paintUnpainted.mutate()}
            disabled={paintUnpainted.isPending}
            data-testid="run-backfill-sheet-paint-unpainted"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {paintUnpainted.isPending ? "Firing…" : "▶ Backfill Sheet Paint (unpainted only)"}
          </Button>
        </div>
      </div>
      <div className="mt-3 border border-slate-200 rounded-md p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-medium text-sm text-slate-800">Remove legacy "Cancelled Order File" tab</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Deletes the stale <b>Cancelled Order File</b> tab from BOTH master workbooks (F1-HQ-Check + F2-AOOA-Check).
              The cancel handler stopped creating this tab in the Feb refactor, but any tab that pre-existed
              still confuses the reviewer. Safe / idempotent — skips masters where the tab is already gone.
            </p>
          </div>
          <Button
            onClick={() => {
              if (!window.confirm("Delete the 'Cancelled Order File' tab from BOTH master workbooks (F1 + F2)?\n\nThis is safe and idempotent — it just removes a legacy tab that shouldn't exist anymore.")) return;
              removeCancelledTab.mutate();
            }}
            disabled={removeCancelledTab.isPending}
            data-testid="run-remove-cancelled-order-tab"
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            {removeCancelledTab.isPending ? "Removing…" : "🗑 Remove Cancelled Order Tab"}
          </Button>
        </div>
      </div>
      <div className="mt-3 border border-slate-200 rounded-md p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-medium text-sm text-slate-800">Purge legacy NA rows from No Mold sheet</div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Removes every row in the <b>No Mold</b> master whose Stage column equals <span className="font-mono">"NA"</span> —
              legacy entries from before the refactor when NA orders were auto-appended to No Mold. From that
              refactor onwards, No Mold only receives <span className="font-mono">Stage=Cancelled</span> rows from a
              Cancel with the "No Mold" reason ticked. Safe / idempotent.
            </p>
          </div>
          <Button
            onClick={() => {
              if (!window.confirm("Delete every row in the No Mold sheet where Stage='NA'?\n\nThese are legacy pre-refactor entries. Cancelled-with-no-mold rows are untouched.")) return;
              purgeNaRows.mutate();
            }}
            disabled={purgeNaRows.isPending}
            data-testid="run-purge-na-rows-no-mold"
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            {purgeNaRows.isPending ? "Purging…" : "🧹 Purge NA Rows"}
          </Button>
        </div>
      </div>
    </section>
  );
}

function PhotoReminderCard() {
  const qc = useQueryClient();
  const { t } = useT();
  const { data: rem, isLoading } = useQuery({
    queryKey: ["photo-reminder-status"],
    queryFn: async () => (await api.get("/photo-reminder/status")).data,
    refetchInterval: 30000,
  });
  const [testEmail, setTestEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("LEPRECON");
  const [orderNum, setOrderNum] = React.useState("55555");

  const test = useMutation({
    mutationFn: async () => (await api.post("/photo-reminder/test", {
      to_email: testEmail.trim(),
      first_name: firstName.trim() || "LEPRECON",
      order_number: orderNum.trim() || "55555",
    })).data,
    onSuccess: (d) => toast.success(`✓ Test email sent to ${d.to}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Test send failed"),
  });
  const connectGmail = useMutation({
    mutationFn: async () => (await api.get("/gmail/connect")).data,
    onSuccess: (d) => {
      const url = d?.authorization_url;
      if (!url) return;
      // Same iframe workaround as Drive: Google's OAuth page refuses to render
      // inside the Emergent preview iframe (SAMEORIGIN), so pop it out.
      const w = window.open(url, "_blank", "noopener");
      if (!w) window.location.href = url;
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to start Gmail OAuth"),
  });
  const disconnectGmail = useMutation({
    mutationFn: async () => (await api.post("/gmail/disconnect")).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["photo-reminder-status"] }); toast.success("Gmail disconnected"); },
  });

  const gmailOn = !!rem?.gmail_connected;
  const smtpOn = !!rem?.smtp_configured;
  const senderReady = gmailOn || smtpOn;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="photo-reminder-card">
      <div className="flex items-center gap-2">
        <span className="text-2xl">📧</span>
        <h2 className="font-semibold text-lg">{t("Photo Reminder Emails")}</h2>
        {isLoading ? (
          <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Checking…</Badge>
        ) : gmailOn ? (
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Gmail Connected · {rem.gmail_email}</Badge>
        ) : smtpOn ? (
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">SMTP Connected · {rem.smtp_user}</Badge>
        ) : (
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Not connected</Badge>
        )}
      </div>
      <p className="text-sm text-slate-600 mt-2">
        Automatically nudges customers whose Pre Orders are still missing VIN / caliper photos. Cadence from the day the order entered the CRM: <b>Week 1 → Week 2 → Week 4</b> (each reminder has its own subject line — edit them in the Template Library below), then the order moves to <b>Abandoned Orders</b> one week after the last reminder (day 35). If photos land in Drive later, the order flips back to Pre Orders automatically.
      </p>

      {!senderReady && (
        <div className="mt-3 text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded p-3">
          <b>Setup:</b> Click <b>Connect Gmail</b> below. A Google consent screen will open — sign in as <code>service@leprecon.plus</code>, grant "Send email on your behalf", and you'll be bounced back here as connected. No App Password required (works with Google Workspace).
        </div>
      )}

      <div className="mt-3 flex gap-2">
        {!gmailOn && (
          <Button
            data-testid="connect-gmail"
            onClick={() => connectGmail.mutate()}
            disabled={connectGmail.isPending}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {connectGmail.isPending ? "Opening Google…" : "🔗 Connect Gmail (OAuth)"}
          </Button>
        )}
        {gmailOn && (
          <Button
            data-testid="disconnect-gmail"
            variant="outline"
            onClick={() => disconnectGmail.mutate()}
            disabled={disconnectGmail.isPending}
          >
            Disconnect Gmail
          </Button>
        )}
      </div>

      {rem && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">From</div>
            <div className="mt-1">{rem.from_email || "—"}</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Pending Pre Orders</div>
            <div className="mt-1"><b>{rem.candidates_pending}</b> order(s) still awaiting photos</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Cadence (days)</div>
            <div className="mt-1 font-mono text-xs">{(rem.intervals_days || []).join(" → ")} · abandon after {rem.abandon_after_days}</div>
          </div>
        </div>
      )}

      <div className="mt-4 border-t border-slate-200 pt-4">
        <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Send a test email</div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">To</Label>
            <Input data-testid="test-reminder-to" placeholder="you@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">First name</Label>
            <Input data-testid="test-reminder-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Order #</Label>
            <Input data-testid="test-reminder-order-num" value={orderNum} onChange={(e) => setOrderNum(e.target.value)} />
          </div>
        </div>
        <Button
          data-testid="test-reminder-send"
          className="mt-3 bg-sky-600 hover:bg-sky-700 text-white"
          disabled={!testEmail.trim() || test.isPending || !senderReady}
          onClick={() => test.mutate()}
        >
          {test.isPending ? "Sending…" : "📧 Send Test Email"}
        </Button>
        {!senderReady && <p className="text-xs text-amber-700 mt-2">Connect Gmail first to enable sending.</p>}
      </div>

      {/* ---- Email template editor (multi-template hub) ---- */}
      <EmailTemplateEditor previewName={firstName} previewOrder={orderNum} />
    </div>
  );
}

const TEMPLATE_HAS_MISSING = (key) => key === "missing_info";
const IS_INSTALL_TPL = (key) => key === "installation_guide";
const INSTALL_CLAMPS = ["AL", "Double", "Single", "Fork", "Gen 1.", "Gen 2.", "Sliding", "Reversed", "Gen 2. Sliding"];

/** Auto-send switch + status for the "Fulfilled Email" (installation guide, 2 days after fulfilled). */
function InstallationAutoSend({ enabled }) {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["installation-email-status"],
    queryFn: async () => (await api.get("/installation-email/status")).data,
  });
  const toggle = useMutation({
    mutationFn: async (on) => (await api.put("/email-templates/installation_guide", { enabled: on })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["installation-email-status"] });
      qc.invalidateQueries({ queryKey: ["email-template", "installation_guide"] });
      toast.success(d?.template?.enabled ? "Auto-send ON — orders fulfilled from now on get the email 2 days later" : "Auto-send OFF — no installation emails will go out");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update"),
  });
  const s = statusQ.data;
  const on = s ? s.enabled : !!enabled;
  return (
    <div className={`rounded-lg border p-3 flex flex-wrap items-center justify-between gap-3 ${on ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="install-autosend-card">
      <div className="text-sm">
        <div className="font-semibold">{on ? "🟢 Auto-send is ON" : "⏸ Auto-send is OFF — preview only"}</div>
        <div className="text-xs text-slate-600 mt-0.5">
          Sends {s?.delay_days ?? 2} days after an order is fulfilled (shipped or picked up). Only orders fulfilled after switching ON are included.
          {on && s && <> · Waiting: <strong data-testid="install-pending">{s.pending}</strong> · Due now: <strong>{s.due_now}</strong></>}
          {s && <> · Sent so far: <strong data-testid="install-sent">{s.sent_total}</strong></>}
          {s && <> · Opened: <strong data-testid="install-opened">{s.opened_total}</strong> · Awaiting open: <strong data-testid="install-awaiting">{s.awaiting_open}</strong>{s.resend_until_opened ? <> · Re-sends every {s.resend_every_days} days (max {s.resend_max}) · re-sent: <strong data-testid="install-resent">{s.resends_total}</strong> · never opened: <strong data-testid="install-unopened">{s.unopened_final}</strong></> : " · re-send OFF"}</>}
          {s?.stopped_untracked > 0 && <> · <span className="text-amber-700" data-testid="install-stopped-untracked">reminders stopped for {s.stopped_untracked} order(s) e-mailed before open-tracking existed</span></>}
        </div>
      </div>
      <Button
        size="sm"
        data-testid="install-autosend-toggle"
        disabled={toggle.isPending}
        onClick={() => {
          if (!on && !window.confirm("Turn Auto-send ON? Every order fulfilled from now on will receive the Fulfilled Email (installation guide + black-envelope note) 2 days later.")) return;
          toggle.mutate(!on);
        }}
        className={on ? "bg-slate-700 hover:bg-slate-800 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
      >
        {toggle.isPending ? "Saving…" : on ? "Turn OFF" : "Turn ON"}
      </Button>
    </div>
  );
}

/** Editor fields specific to the Fulfilled Email (installation_guide) template. */
function InstallationGuideFields({ draft, patch, patchListItem, sample, setSample }) {
  const ta = "w-full min-h-[64px] rounded border border-slate-300 px-2 py-1 text-sm";
  const patchLink = (k, v) => patch("clamp_links", { ...(draft.clamp_links || {}), [k]: v });
  return (
    <>
      {[0, 1].map((i) => (
        <div key={`intro-${i}`}>
          <Label className="text-xs">Intro paragraph {i + 1}</Label>
          <textarea data-testid={`tpl-install-intro-${i}`} className={ta} value={(draft.paragraphs_intro || [])[i] || ""} onChange={(e) => patchListItem("paragraphs_intro", i, e.target.value)} />
        </div>
      ))}
      <div>
        <Label className="text-xs">General installation page (button link)</Label>
        <Input data-testid="tpl-install-general-url" value={draft.general_url || ""} onChange={(e) => patch("general_url", e.target.value)} />
      </div>
      <div className="rounded border border-emerald-200 bg-emerald-50 p-3 space-y-2">
        <div className="text-[11px] uppercase tracking-widest font-mono text-emerald-800">Per-clamp paragraphs — tags: <code>{"{front_clamp} {front_url} {rear_clamp} {rear_url}"}</code></div>
        <div>
          <Label className="text-xs">Steps title</Label>
          <Input value={draft.steps_title || ""} onChange={(e) => patch("steps_title", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Front cover paragraph</Label>
          <textarea data-testid="tpl-install-front" className={ta} value={draft.front_paragraph || ""} onChange={(e) => patch("front_paragraph", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Rear cover paragraph</Label>
          <textarea data-testid="tpl-install-rear" className={ta} value={draft.rear_paragraph || ""} onChange={(e) => patch("rear_paragraph", e.target.value)} />
        </div>
        <div className="text-[11px] text-emerald-800 pt-1">Clamp → video link</div>
        <div className="grid grid-cols-1 gap-1.5">
          {INSTALL_CLAMPS.map((k) => (
            <div key={k} className="flex items-center gap-2">
              <span className="w-28 shrink-0 text-xs font-mono">{k}</span>
              <Input className="h-8 text-xs" data-testid={`tpl-install-link-${k.replace(/[^a-z0-9]/gi, "").toLowerCase()}`} value={(draft.clamp_links || {})[k] || ""} onChange={(e) => patchLink(k, e.target.value)} />
            </div>
          ))}
        </div>
      </div>
      <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-2">
        <div className="text-[11px] uppercase tracking-widest font-mono text-amber-800">Clamp-gap tip — tags: <code>{"{adjust_clamps} {adjust_url}"}</code></div>
        <textarea data-testid="tpl-install-adjust" className={ta} value={draft.adjustment_paragraph || ""} onChange={(e) => patch("adjustment_paragraph", e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Adjustment guide link</Label>
            <Input value={draft.adjustment_url || ""} onChange={(e) => patch("adjustment_url", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Clamps that get the tip</Label>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="tpl-adjustment-clamps">
              {INSTALL_CLAMPS.map((k) => {
                const on = (draft.adjustment_clamps || []).includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`tpl-adjust-clamp-${k.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                    onClick={() => patch("adjustment_clamps", on ? (draft.adjustment_clamps || []).filter((x) => x !== k) : [...(draft.adjustment_clamps || []), k])}
                    className={`px-2 py-1 rounded border text-xs font-mono ${on ? "bg-amber-600 border-amber-700 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                  >
                    {on ? "✓ " : ""}{k}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="rounded border border-blue-200 bg-blue-50 p-3 space-y-2">
        <div className="text-[11px] uppercase tracking-widest font-mono text-blue-800">Label guide — USA-flag orders only · tag: <code>{"{label_url}"}</code></div>
        <textarea data-testid="tpl-install-label" className={ta} value={draft.label_paragraph || ""} onChange={(e) => patch("label_paragraph", e.target.value)} />
        <div>
          <Label className="text-xs">Label guide link <span className="text-slate-400">(empty = default leprecon.plus label-installation page)</span></Label>
          <Input data-testid="tpl-install-label-url" placeholder="https://leprecon.plus/pages/installation/label-installation" value={draft.label_url || ""} onChange={(e) => patch("label_url", e.target.value)} />
        </div>
      </div>
      {[0, 1].map((i) => (
        <div key={`outro-${i}`}>
          <Label className="text-xs">Closing paragraph {i + 1}</Label>
          <textarea data-testid={`tpl-install-outro-${i}`} className={ta} value={(draft.paragraphs_outro || [])[i] || ""} onChange={(e) => patchListItem("paragraphs_outro", i, e.target.value)} />
        </div>
      ))}
      <div className="rounded border border-orange-300 bg-orange-50 p-3 space-y-2" data-testid="tpl-install-important">
        <div className="text-[11px] uppercase tracking-widest font-mono text-orange-800">⚠️ Black-envelope note — shown at the bottom of the e-mail · "Must read" pointer under the intro</div>
        <div>
          <Label className="text-xs">"Must read" pointer (top of the e-mail · empty = hidden)</Label>
          <textarea data-testid="tpl-install-pointer" className={ta} value={draft.must_read_pointer || ""} onChange={(e) => patch("must_read_pointer", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Important note title</Label>
          <Input data-testid="tpl-install-important-title" value={draft.important_title || ""} onChange={(e) => patch("important_title", e.target.value)} />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={`imp-${i}`}>
            <Label className="text-xs">Important note paragraph {i + 1}</Label>
            <textarea data-testid={`tpl-install-important-${i}`} className={ta} value={(draft.important_paragraphs || [])[i] || ""} onChange={(e) => patchListItem("important_paragraphs", i, e.target.value)} />
          </div>
        ))}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Photo <span className="text-slate-400">(CRM path or full https:// link · empty = no photo)</span></Label>
            <Input data-testid="tpl-install-important-image" value={draft.important_image_path || ""} onChange={(e) => patch("important_image_path", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Photo caption</Label>
            <Input data-testid="tpl-install-important-caption" value={draft.important_image_caption || ""} onChange={(e) => patch("important_image_caption", e.target.value)} />
          </div>
        </div>
      </div>
      <div className="rounded border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="tpl-install-resend">
        <div className="text-[11px] uppercase tracking-widest font-mono text-rose-800">Re-send until opened — every e-mail carries an open pixel + tracked links</div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.resend_until_opened !== false} onChange={(e) => patch("resend_until_opened", e.target.checked)} data-testid="tpl-install-resend-on" />
          Keep re-sending while the customer has not opened or clicked the e-mail
        </label>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-xs">Every · days</Label><Input type="number" min={1} data-testid="tpl-install-resend-every" value={draft.resend_every_days ?? 2} onChange={(e) => patch("resend_every_days", Number(e.target.value))} /></div>
          <div><Label className="text-xs">Max re-sends</Label><Input type="number" min={0} data-testid="tpl-install-resend-max" value={draft.resend_max ?? 4} onChange={(e) => patch("resend_max", Number(e.target.value))} /></div>
          <div className="col-span-3"><Label className="text-xs">Reminder subject</Label><Input data-testid="tpl-install-resend-subject" value={draft.subject_reminder || ""} onChange={(e) => patch("subject_reminder", e.target.value)} /></div>
        </div>
      </div>
      <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-2">
        <div className="text-[11px] uppercase tracking-widest font-mono text-slate-600">Need help? block</div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Title</Label><Input value={draft.help_title || ""} onChange={(e) => patch("help_title", e.target.value)} /></div>
          <div><Label className="text-xs">Phone</Label><Input data-testid="tpl-install-help-phone" value={draft.help_phone || ""} onChange={(e) => patch("help_phone", e.target.value)} /></div>
          <div><Label className="text-xs">Hours</Label><Input data-testid="tpl-install-help-hours" value={draft.help_hours || ""} onChange={(e) => patch("help_hours", e.target.value)} /></div>
          <div><Label className="text-xs">Video chat booking link</Label><Input data-testid="tpl-install-video-url" value={draft.video_chat_url || ""} onChange={(e) => patch("video_chat_url", e.target.value)} /></div>
        </div>
      </div>
      <div className="rounded border border-violet-200 bg-violet-50 p-3">
        <div className="text-[11px] uppercase tracking-widest font-mono text-violet-800 mb-2">Preview with a sample order</div>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <label className="inline-flex items-center gap-1">F-Clamp
            <select data-testid="install-preview-fclamp" className="h-8 rounded border border-slate-300 px-1 bg-white" value={sample.f_clamp} onChange={(e) => setSample({ ...sample, f_clamp: e.target.value })}>
              <option value="">— none —</option>{INSTALL_CLAMPS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-1">R-Clamp
            <select data-testid="install-preview-rclamp" className="h-8 rounded border border-slate-300 px-1 bg-white" value={sample.r_clamp} onChange={(e) => setSample({ ...sample, r_clamp: e.target.value })}>
              <option value="">— none —</option>{INSTALL_CLAMPS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-1 bg-white rounded px-2 py-1 border border-violet-300">
            <input type="checkbox" data-testid="install-preview-us" checked={sample.us_order} onChange={(e) => setSample({ ...sample, us_order: e.target.checked })} /> USA-flag order
          </label>
        </div>
      </div>
    </>
  );
}

function EmailTemplateEditor({ previewName, previewOrder }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = React.useState(false);
  const [activeKey, setActiveKey] = React.useState("photo_reminder");
  const [drafts, setDrafts] = React.useState({}); // key -> draft
  const [dirtyKey, setDirtyKey] = React.useState(null);
  const [missingPreview, setMissingPreview] = React.useState({ vin: true, rim: true, front: false, rear: false, custom_logo: false });
  const [installSample, setInstallSample] = React.useState({ f_clamp: "Fork", r_clamp: "AL", us_order: true });
  const [journeyColor, setJourneyColor] = React.useState("RED");
  const journeyLibQ = useQuery({ queryKey: ["journey-settings"], queryFn: async () => (await api.get("/journey/settings")).data, enabled: expanded && IS_JOURNEY_TPL(activeKey) });
  // Add / Remove template dialogs
  const [newOpen, setNewOpen] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [newDescription, setNewDescription] = React.useState("");
  const [removeOpen, setRemoveOpen] = React.useState(false);

  const listQ = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => (await api.get("/email-templates")).data,
    enabled: expanded,
  });

  const detailQ = useQuery({
    queryKey: ["email-template", activeKey],
    queryFn: async () => (await api.get(`/email-templates/${activeKey}`)).data,
    enabled: expanded,
  });

  React.useEffect(() => {
    if (detailQ.data?.template && !drafts[activeKey]) {
      setDrafts((d) => ({ ...d, [activeKey]: detailQ.data.template }));
    }
  }, [detailQ.data, activeKey, drafts]);

  const draft = drafts[activeKey];
  const dirty = dirtyKey === activeKey;

  // Compute which missing fields to send to the preview endpoint. For
  // missing_info: only pass the UNCHECKED items (those that are missing).
  const previewMissingFields = TEMPLATE_HAS_MISSING(activeKey)
    ? Object.keys(missingPreview).filter((k) => !missingPreview[k])
    : undefined;

  const previewQ = useQuery({
    queryKey: ["email-template-preview", activeKey, draft, previewName, previewOrder, previewMissingFields?.join(","), IS_INSTALL_TPL(activeKey) ? JSON.stringify(installSample) : "", IS_JOURNEY_TPL(activeKey) ? journeyColor : ""],
    queryFn: async () => (await api.post(`/email-templates/${activeKey}/preview`, {
      first_name: previewName || "LEPRECON",
      order_number: previewOrder || "55555",
      template: draft,
      missing_fields: previewMissingFields,
      ...(IS_INSTALL_TPL(activeKey) ? { f_clamp: installSample.f_clamp || null, r_clamp: installSample.r_clamp || null, us_order: installSample.us_order } : {}),
      ...(IS_JOURNEY_TPL(activeKey) ? { color: journeyColor } : {}),
    })).data,
    enabled: expanded && !!draft,
  });

  const save = useMutation({
    mutationFn: async () => (await api.put(`/email-templates/${activeKey}`, draft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-template", activeKey] });
      toast.success("Template saved");
      setDirtyKey(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const reset = useMutation({
    mutationFn: async () => (await api.post(`/email-templates/${activeKey}/reset`)).data,
    onSuccess: (d) => {
      setDrafts((prev) => ({ ...prev, [activeKey]: d.template }));
      setDirtyKey(null);
      qc.invalidateQueries({ queryKey: ["email-template", activeKey] });
      toast.success("Reset to default template");
    },
  });
  const createTpl = useMutation({
    mutationFn: async (payload) => (await api.post("/email-templates", payload)).data,
    onSuccess: (d) => {
      const key = d?.template?.key;
      qc.invalidateQueries({ queryKey: ["email-templates"] });
      if (key) {
        setActiveKey(key);
        setDrafts((prev) => ({ ...prev, [key]: d.template }));
      }
      setNewOpen(false);
      setNewName("");
      setNewDescription("");
      toast.success(`Template "${d.template.name}" created`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create template"),
  });
  const removeTpl = useMutation({
    mutationFn: async () => (await api.delete(`/email-templates/${activeKey}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-templates"] });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      const removedKey = activeKey;
      setActiveKey("photo_reminder");
      setRemoveOpen(false);
      toast.success(`Template "${removedKey}" removed`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove template"),
  });
  // Whether the currently-active template is a custom (deletable) one.
  const activeTplMeta = (listQ.data || []).find((t) => t.key === activeKey);
  const isCustomTpl = !!activeTplMeta?.custom;

  const patch = (field, value) => {
    setDrafts((prev) => ({ ...prev, [activeKey]: { ...(prev[activeKey] || {}), [field]: value } }));
    setDirtyKey(activeKey);
  };
  const patchListItem = (fieldName, idx, value) => {
    const list = [...((draft?.[fieldName]) || [])];
    list[idx] = value;
    patch(fieldName, list);
  };
  const patchMissingParagraph = (key, value) => {
    patch("missing_paragraphs", { ...(draft?.missing_paragraphs || {}), [key]: value });
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-4" data-testid="email-template-editor">
      <button
        type="button"
        className="w-full flex items-center justify-between text-left"
        onClick={() => setExpanded((v) => !v)}
        data-testid="email-templates-toggle"
      >
        <div>
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Customer Email Templates</div>
          <div className="text-sm font-semibold text-slate-800 mt-0.5">Template Library</div>
          <div className="text-xs text-slate-500">Preview and edit every automated + manual email the CRM sends. Uses <code>{"{first_name}"}</code> and <code>{"{order_number}"}</code> as merge tags.</div>
        </div>
        <span className="text-slate-500 text-sm">{expanded ? "▲ Hide" : "▼ Show"}</span>
      </button>

      {expanded && (
        <div className="mt-4">
          {/* Template selector tabs + Add new */}
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div className="flex flex-wrap gap-2" data-testid="template-tabs">
              {(listQ.data || []).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveKey(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${activeKey === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                >
                  <span>{t.name}</span>
                  {t.custom && (
                    <span
                      className={`text-[9px] uppercase tracking-widest font-mono rounded px-1 py-[1px] border ${activeKey === t.key ? "bg-white/20 border-white/40 text-white" : "bg-amber-50 border-amber-300 text-amber-800"}`}
                      title="Custom template"
                    >custom</span>
                  )}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              type="button"
              onClick={() => setNewOpen(true)}
              data-testid="new-template-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shrink-0"
              title="Create a brand new custom email template"
            >
              ➕ New Template
            </Button>
          </div>
          {detailQ.data?.template?.description && (
            <p className="text-xs text-slate-500 italic mb-2">{detailQ.data.template.description}</p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              {!draft ? (
                <p className="text-sm text-slate-500">Loading template…</p>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">{activeKey === "photo_reminder" ? "Subject · Week 1 (1st reminder)" : "Subject"}</Label>
                    <Input data-testid="tpl-subject" value={draft.subject || ""} onChange={(e) => patch("subject", e.target.value)} />
                  </div>
                  {activeKey === "photo_reminder" && (
                    <>
                      <div>
                        <Label className="text-xs">Subject · Week 2 (2nd reminder)</Label>
                        <Input data-testid="tpl-subject-2" value={draft.subject_2 || ""} onChange={(e) => patch("subject_2", e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Subject · Week 4 (final reminder — order is abandoned one week later)</Label>
                        <Input data-testid="tpl-subject-3" value={draft.subject_3 || ""} onChange={(e) => patch("subject_3", e.target.value)} />
                      </div>
                    </>
                  )}
                  <div>
                    <Label className="text-xs">Greeting <span className="text-slate-400">(use <code>{"{first_name}"}</code>)</span></Label>
                    <Input data-testid="tpl-greeting" value={draft.greeting || ""} onChange={(e) => patch("greeting", e.target.value)} />
                  </div>

                  {/* Standard paragraphs */}
                  {!TEMPLATE_HAS_MISSING(activeKey) && (draft.paragraphs || []).map((p, i) => (
                    <div key={i}>
                      <Label className="text-xs">Paragraph {i + 1}</Label>
                      <textarea
                        data-testid={`tpl-p-${i}`}
                        className="w-full min-h-[72px] rounded border border-slate-300 px-2 py-1 text-sm"
                        value={p}
                        onChange={(e) => patchListItem("paragraphs", i, e.target.value)}
                      />
                    </div>
                  ))}

                  {/* Fulfilled Email (installation_guide, Day-2 auto email) — switch + specific fields */}
                  {IS_INSTALL_TPL(activeKey) && (
                    <>
                      <InstallationAutoSend enabled={draft.enabled} />
                      <InstallationGuideFields draft={draft} patch={patch} patchListItem={patchListItem} sample={installSample} setSample={setInstallSample} />
                    </>
                  )}

                  {/* ✉️ Journey e-mails — per-milestone switch + designed-layout fields */}
                  {IS_JOURNEY_TPL(activeKey) && (
                    <>
                      <JourneyAutoSend tplKey={activeKey} enabled={!!draft.enabled} />
                      <JourneyFields draft={{ ...draft, key: activeKey }} patch={patch} patchListItem={patchListItem} sampleColor={journeyColor} setSampleColor={setJourneyColor}
                        library={journeyLibQ.data?.library || []} libraryBase={journeyLibQ.data?.library_base || ""} />
                    </>
                  )}

                  {/* Missing Info template — intro + per-field paragraphs + outro */}
                  {TEMPLATE_HAS_MISSING(activeKey) && (
                    <>
                      <div>
                        <Label className="text-xs">Intro paragraph</Label>
                        <textarea
                          data-testid="tpl-intro"
                          className="w-full min-h-[72px] rounded border border-slate-300 px-2 py-1 text-sm"
                          value={(draft.paragraphs_intro || [""])[0] || ""}
                          onChange={(e) => patchListItem("paragraphs_intro", 0, e.target.value)}
                        />
                      </div>
                      <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-2">
                        <div className="text-[11px] uppercase tracking-widest font-mono text-amber-800">Per-field paragraphs (one added to the email per missing checkbox)</div>
                        {["vin", "rim", "front", "rear", "custom_logo"].map((k) => (
                          <div key={k}>
                            <Label className="text-xs">{k === "custom_logo" ? "CUSTOM LOGO" : k.toUpperCase()} paragraph</Label>
                            <textarea
                              data-testid={`tpl-missing-${k}`}
                              className="w-full min-h-[54px] rounded border border-slate-300 px-2 py-1 text-sm"
                              value={(draft.missing_paragraphs && draft.missing_paragraphs[k]) || ""}
                              onChange={(e) => patchMissingParagraph(k, e.target.value)}
                            />
                          </div>
                        ))}
                        <div className="text-[11px] text-amber-800 mt-2">Preview: toggle which fields are missing —</div>
                        <div className="flex gap-2 flex-wrap">
                          {["vin", "rim", "front", "rear", "custom_logo"].map((k) => (
                            <label key={k} className="inline-flex items-center gap-1 text-xs bg-white rounded px-2 py-1 border border-amber-300">
                              <input type="checkbox" checked={missingPreview[k]} onChange={(e) => setMissingPreview((m) => ({ ...m, [k]: e.target.checked }))} />
                              <span>{missingPreview[k] ? "✓ has" : "✗ missing"} {k === "custom_logo" ? "logo" : k}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Outro paragraph 1</Label>
                        <textarea
                          className="w-full min-h-[54px] rounded border border-slate-300 px-2 py-1 text-sm"
                          value={(draft.paragraphs_outro || [""])[0] || ""}
                          onChange={(e) => patchListItem("paragraphs_outro", 0, e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Outro paragraph 2 (thanks)</Label>
                        <textarea
                          className="w-full min-h-[54px] rounded border border-slate-300 px-2 py-1 text-sm"
                          value={(draft.paragraphs_outro || ["", ""])[1] || ""}
                          onChange={(e) => patchListItem("paragraphs_outro", 1, e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Button text</Label>
                      <Input data-testid="tpl-btn-text" value={draft.button_text || ""} onChange={(e) => patch("button_text", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Button color</Label>
                      <div className="flex gap-1">
                        <input data-testid="tpl-btn-color" type="color" value={draft.button_color || "#B6D900"} onChange={(e) => patch("button_color", e.target.value)} className="w-10 h-9 rounded border border-slate-300" />
                        <Input value={draft.button_color || ""} onChange={(e) => patch("button_color", e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Button text color</Label>
                      <div className="flex gap-1">
                        <input type="color" value={draft.button_text_color || "#000000"} onChange={(e) => patch("button_text_color", e.target.value)} className="w-10 h-9 rounded border border-slate-300" />
                        <Input value={draft.button_text_color || ""} onChange={(e) => patch("button_text_color", e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Include button in this email?</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="checkbox"
                        data-testid="tpl-include-button"
                        checked={draft.include_button !== false}
                        onChange={(e) => patch("include_button", e.target.checked)}
                      />
                      <span className="text-xs text-slate-600">Uncheck to hide the CTA button (e.g. for "Thank You" emails). The "CTA link text" below still appears as a text link when filled in.</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">CTA link text <span className="text-slate-400">(text link under the button — shown whenever filled in, even with the button unchecked)</span></Label>
                    <Input data-testid="tpl-cta" value={draft.cta_link_text || ""} onChange={(e) => patch("cta_link_text", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Button link <span className="text-slate-400">{activeKey === "google_review" ? "(the ⭐ button always opens the tracked review link; this link is used by the CTA text link above)" : activeKey === "installation_guide" ? "(leave empty → installation videos page)" : "(leave empty → photo-upload page; e.g. a Calendly booking link)"}</span></Label>
                    <Input data-testid="tpl-button-url" placeholder="https://…" value={draft.button_url || ""} onChange={(e) => patch("button_url", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Team name</Label>
                      <Input value={draft.team_name || ""} onChange={(e) => patch("team_name", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Phone</Label>
                      <Input value={draft.phone || ""} onChange={(e) => patch("phone", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input value={draft.email || ""} onChange={(e) => patch("email", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Website</Label>
                      <Input value={draft.website || ""} onChange={(e) => patch("website", e.target.value)} />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2 flex-wrap">
                    <Button data-testid="tpl-save" onClick={() => save.mutate()} disabled={!dirty || save.isPending} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      {save.isPending ? "Saving…" : "💾 Save Template"}
                    </Button>
                    {!isCustomTpl && (
                      <Button data-testid="tpl-reset" variant="outline" onClick={() => reset.mutate()} disabled={reset.isPending}>
                        {reset.isPending ? "Resetting…" : "↺ Reset to Default"}
                      </Button>
                    )}
                    {isCustomTpl && (
                      <Button
                        data-testid="tpl-remove"
                        variant="outline"
                        onClick={() => setRemoveOpen(true)}
                        disabled={removeTpl.isPending}
                        className="border-red-500 text-red-700 hover:bg-red-50"
                        title="Delete this custom template permanently"
                      >
                        🗑 Remove Template
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden bg-white flex flex-col">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Live Preview</div>
                <div className="text-xs text-slate-500 truncate max-w-[60%]">Subject: <span className="text-slate-700">{previewQ.data?.subject || "…"}</span></div>
              </div>
              <iframe
                data-testid="tpl-preview"
                title="Email preview"
                className="w-full min-h-[520px] bg-white"
                srcDoc={previewQ.data?.html || "<em>Loading preview…</em>"}
              />
            </div>
          </div>
        </div>
      )}

      {/* New Template dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-template-dialog">
          <DialogHeader>
            <DialogTitle>Create New Email Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Template name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="new-tpl-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Follow Up Check"
                autoFocus
              />
              <p className="text-[11px] text-slate-500 mt-1">Used as the tab label. A safe key is auto-generated from the name.</p>
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <textarea
                data-testid="new-tpl-description"
                className="w-full min-h-[54px] rounded border border-slate-300 px-2 py-1 text-sm"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Short note about when this email fires. Shown under the tabs."
              />
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
              After the template is created you can edit subject, paragraphs, button, and preview it live in this same panel.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setNewOpen(false)} disabled={createTpl.isPending}>Cancel</Button>
              <Button
                data-testid="new-tpl-submit"
                onClick={() => createTpl.mutate({ name: newName.trim(), description: newDescription.trim() || null })}
                disabled={!newName.trim() || createTpl.isPending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {createTpl.isPending ? "Creating…" : "➕ Create Template"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Template confirmation */}
      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent data-testid="remove-template-dialog">
          <DialogHeader>
            <DialogTitle>Remove template?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              You're about to permanently delete the custom template <span className="font-mono font-semibold">{activeTplMeta?.name || activeKey}</span>. This cannot be undone.
            </p>
            <p className="text-xs text-slate-500">Built-in templates cannot be removed — they can only be reset. Custom templates linked to a Cancel Reason must be detached first.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setRemoveOpen(false)} disabled={removeTpl.isPending}>Cancel</Button>
              <Button
                data-testid="remove-tpl-confirm"
                onClick={() => removeTpl.mutate()}
                disabled={removeTpl.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {removeTpl.isPending ? "Removing…" : "🗑 Remove Permanently"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DangerZone() {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState("");
  const [keepList, setKeepList] = React.useState("");
  const [result, setResult] = React.useState(null);

  const wipe = useMutation({
    mutationFn: async () => {
      const keep_po_numbers = keepList
        .split(/[\s,]+/)
        .map((s) => s.replace(/^#/, "").trim())
        .filter(Boolean);
      const r = await api.post("/admin/wipe-orders", {
        confirm: confirmText,
        keep_po_numbers,
      });
      return r.data;
    },
    onSuccess: (d) => {
      setResult(d);
      toast.success(`Deleted ${d.deleted_orders} order(s) · ${d.remaining_orders} kept`);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Wipe failed"),
  });

  const canSubmit = confirmText === "DELETE ALL ORDERS" && !wipe.isPending;

  const closeAndReset = () => {
    setOpen(false);
    setConfirmText("");
    setKeepList("");
    setResult(null);
  };

  return (
    <div className="bg-white border-2 border-red-300 rounded-lg p-6" data-testid="danger-zone-card">
      <div className="flex items-center gap-2">
        <span className="text-2xl">🚨</span>
        <h2 className="font-semibold text-lg text-red-800">{t("Danger Zone")}</h2>
      </div>
      <p className="text-sm text-slate-600 mt-2">
        Destructive operations. These actions cannot be undone. Only Super Admin can access this section.
      </p>

      {/* Safe additive import — sits above the destructive actions so it's
          impossible to confuse with the wipe. Imports skip PO numbers that
          already exist in the DB. */}
      <div
        className="mt-4 border border-emerald-300 bg-emerald-50/60 rounded-md p-4 flex items-center justify-between gap-4"
        data-testid="safe-shopify-import-card"
      >
        <div>
          <div className="font-semibold text-emerald-900">Import Orders from Shopify (Safe)</div>
          <p className="text-xs text-slate-600 mt-1 max-w-md">
            Pulls orders from Shopify by order-number list or via a CSV export.
            <strong className="text-emerald-800"> Existing orders are kept as-is</strong> — duplicates are skipped, nothing is deleted or overwritten.
          </p>
        </div>
        <ShopifyBulkImportDialog onImported={() => qc.invalidateQueries()} />
      </div>

      <div className="mt-4 border border-red-200 bg-red-50/50 rounded-md p-4 flex items-center justify-between gap-4">
        <div>
          <div className="font-semibold text-slate-900">Hard-Delete Orders by PO #</div>
          <p className="text-xs text-slate-600 mt-1 max-w-md">
            Permanently removes a specific list of orders (comma or space separated PO numbers) with no client emails,
            no Sheet writes, no cancellation flow. Also drops their billing rows, refunds, email logs, alert actions,
            and reminders. Idempotent — skips PO numbers that don't exist.
          </p>
        </div>
        <HardDeleteOrdersDialog onDone={() => qc.invalidateQueries()} />
      </div>

      <div className="mt-4 border border-teal-200 bg-teal-50/50 rounded-md p-4 flex items-center justify-between gap-4">
        <div>
          <div className="font-semibold text-slate-900">Repair Lead → PO Conversion</div>
          <p className="text-xs text-slate-600 mt-1 max-w-md">
            For a PO that came from a Lead but shows the "Lead LD-…" placeholder customer or lost its factory codes:
            attaches the Shopify twin (customer, shipping, Shopify #), pulls the codes back from the Lead trail,
            retires the duplicate and moves the order to Advance when both codes are present.
          </p>
        </div>
        <RepairLeadOrderTool onDone={() => qc.invalidateQueries()} />
      </div>

      <div className="mt-4 border border-red-200 bg-red-50/50 rounded-md p-4 flex items-center justify-between gap-4">
        <div>
          <div className="font-semibold text-slate-900">Wipe All Orders</div>
          <p className="text-xs text-slate-600 mt-1 max-w-md">
            Deletes every order in every tab, plus related billing batches and refunds.
            Customers, catalog inventory, users, and integrations are kept.
            Useful when starting fresh (e.g., after test data). Optionally keep specific PO numbers.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="wipe-orders-btn"
        >
          🗑 Wipe All Orders
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeAndReset())}>
        <DialogContent className="max-w-lg" data-testid="wipe-orders-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-800">🚨 Wipe All Orders</DialogTitle>
            <DialogDescription>
              This will permanently delete <b>every order</b> in the system, along with related
              batches and refunds. This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3 text-sm space-y-1" data-testid="wipe-orders-result">
              <div className="font-semibold text-emerald-900">✓ Wipe complete</div>
              <div className="text-xs font-mono">Deleted orders: <b>{result.deleted_orders}</b></div>
              <div className="text-xs font-mono">Deleted batches: <b>{result.deleted_batches}</b></div>
              <div className="text-xs font-mono">Deleted refunds: <b>{result.deleted_refunds}</b></div>
              <div className="text-xs font-mono">Remaining orders: <b>{result.remaining_orders}</b></div>
              {result.kept_po_numbers?.length > 0 && (
                <div className="text-xs font-mono pt-1 border-t border-emerald-200">
                  Kept: {result.kept_po_numbers.join(", ")}
                </div>
              )}
              <div className="text-xs text-slate-700 pt-2">
                Next: use <b>Import from Shopify → Upload CSV</b> on the Pre Orders page to re-seed
                with real orders.
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={closeAndReset} data-testid="wipe-orders-done">Done</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Keep these PO numbers (optional · comma / newline separated)</Label>
                <Input
                  data-testid="wipe-keep-list"
                  value={keepList}
                  onChange={(e) => setKeepList(e.target.value)}
                  placeholder="e.g. 4802, 5001 (leave blank to wipe everything)"
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">
                  Type <b className="font-mono text-red-700">DELETE ALL ORDERS</b> to confirm
                </Label>
                <Input
                  data-testid="wipe-confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE ALL ORDERS"
                  className={`font-mono text-sm ${
                    confirmText && confirmText !== "DELETE ALL ORDERS" ? "border-red-500" : ""
                  }`}
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={closeAndReset}
                  disabled={wipe.isPending}
                  data-testid="wipe-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => wipe.mutate()}
                  disabled={!canSubmit}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                  data-testid="wipe-confirm-submit"
                >
                  {wipe.isPending ? "Wiping…" : "🗑 Permanently Delete"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Repair Lead → PO conversion (placeholder customer / missing codes).
function RepairLeadOrderTool({ onDone }) {
  const [po, setPo] = React.useState("");
  const [result, setResult] = React.useState(null);
  const run = useMutation({
    mutationFn: () => api.post("/admin/repair-lead-order", { po: po.trim() }),
    onSuccess: (r) => { setResult(r.data); toast.success(`${r.data.po_number} — ${r.data.actions.join("; ")}`); onDone?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Repair failed"),
  });
  return (
    <div className="flex flex-col items-end gap-2 shrink-0">
      <div className="flex items-center gap-2">
        <Input data-testid="repair-lead-po" value={po} onChange={(e) => setPo(e.target.value)} placeholder="PO-5218" className="h-9 w-36 font-mono" />
        <Button data-testid="repair-lead-run" disabled={!po.trim() || run.isPending} onClick={() => run.mutate()} className="bg-teal-700 hover:bg-teal-800 text-white">
          🛠 {run.isPending ? "Repairing…" : "Repair"}
        </Button>
      </div>
      {result && (
        <div className="text-[11px] font-mono text-slate-700 max-w-md text-right" data-testid="repair-lead-result">
          <div><b>{result.po_number}</b> · {result.status} · {result.customer?.name || "—"}{result.customer_still_placeholder ? " ⚠ still placeholder" : ""}</div>
          <div>codes: {result.front_cover_code || "—"} / {result.rear_cover_code || "—"} · twin: {result.twin_found ? "merged" : "none"}</div>
          <div className="text-slate-500">{result.actions.join(" · ")}</div>
        </div>
      )}
    </div>
  );
}

// Hard-Delete Orders by PO # — Super Admin only. Takes a free-text list of
// PO numbers (comma / space / newline separated) and calls
// POST /api/admin/hard-delete-orders. No client emails, no Sheet writes.
// ---------------------------------------------------------------------------
function HardDeleteOrdersDialog({ onDone }) {
  const [open, setOpen] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState("");
  const [poList, setPoList] = React.useState("");
  const [result, setResult] = React.useState(null);

  const runDelete = useMutation({
    mutationFn: async () => {
      const pos = poList
        .split(/[\s,]+/)
        .map((s) => s.replace(/^#/, "").trim())
        .filter(Boolean);
      const r = await api.post("/admin/hard-delete-orders", { pos });
      return r.data;
    },
    onSuccess: (d) => {
      setResult(d);
      const deleted = d.deleted ?? 0;
      toast.success(`Hard-delete complete · ${deleted} order(s) removed`);
      if (onDone) onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hard-delete failed"),
  });

  const parsedCount = poList.split(/[\s,]+/).map((s) => s.replace(/^#/, "").trim()).filter(Boolean).length;
  const canSubmit = confirmText === "HARD DELETE" && parsedCount > 0 && !runDelete.isPending;

  const closeAndReset = () => {
    setOpen(false);
    setConfirmText("");
    setPoList("");
    setResult(null);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="bg-red-700 hover:bg-red-800 text-white flex-shrink-0"
        data-testid="hard-delete-orders-btn"
      >
        🗑 Hard-Delete Orders
      </Button>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeAndReset())}>
        <DialogContent className="max-w-lg" data-testid="hard-delete-orders-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-800">🗑 Hard-Delete Orders</DialogTitle>
            <DialogDescription>
              Permanently removes the listed orders. No client emails are sent. Idempotent — PO numbers that
              don't exist are skipped and reported as such.
            </DialogDescription>
          </DialogHeader>
          {!result && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono">PO Numbers</Label>
                <textarea
                  value={poList}
                  onChange={(e) => setPoList(e.target.value)}
                  rows={4}
                  placeholder="54758, 4962"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
                  data-testid="hard-delete-orders-list"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Separate with commas, spaces, or new lines. Both <span className="font-mono">PO-4867</span> and
                  <span className="font-mono"> 4867</span> are accepted. Duplicate PO? Add the stage:
                  <span className="font-mono"> 5163:pre_order</span> deletes only that copy.
                  {parsedCount > 0 && <span className="ml-1 text-slate-700">· {parsedCount} PO(s) parsed</span>}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono">Type "HARD DELETE" to confirm</Label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="HARD DELETE"
                  className="mt-1 font-mono"
                  data-testid="hard-delete-orders-confirm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button variant="outline" onClick={closeAndReset}>Cancel</Button>
                <Button
                  onClick={() => runDelete.mutate()}
                  disabled={!canSubmit}
                  data-testid="hard-delete-orders-submit"
                  className="bg-red-700 hover:bg-red-800 text-white"
                >
                  {runDelete.isPending ? "Deleting…" : `Delete ${parsedCount || ""} Order(s)`}
                </Button>
              </div>
            </div>
          )}
          {result && (
            <div className="space-y-2 text-sm">
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
                <div className="font-semibold">✓ Complete · {result.deleted} order(s) removed</div>
              </div>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-auto" data-testid="hard-delete-orders-results">
                {(result.results || []).map((r, i) => (
                  <div key={i} className="p-2 text-xs font-mono">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-800">{r.po}</span>
                      <span className={
                        r.status === "deleted" ? "text-emerald-700 font-semibold" :
                        r.status === "not_found" ? "text-slate-500" :
                        r.status === "ambiguous" ? "text-amber-700 font-semibold" :
                        "text-red-700"
                      }>
                        {r.status}{r.detail ? ` · ${r.detail}` : ""}
                      </span>
                    </div>
                    {r.status === "ambiguous" && (
                      <div className="mt-1 text-amber-800" data-testid={`hard-delete-ambiguous-${i}`}>
                        {(r.matches || []).map((m, j) => (
                          <div key={j}>· {m.status} — created {m.created_at ? new Date(m.created_at).toLocaleString() : "—"}</div>
                        ))}
                        <div className="mt-0.5 text-slate-600">{r.hint}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={closeAndReset}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Password Vault — Super Admin only. Reads plain-text passwords from the
// backend (`GET /admin/user-passwords`). Passwords are stored in plaintext
// alongside the bcrypt hash starting Feb 28, 2026 — see the seed +
// /auth/register + /team CRUD paths. Custom accounts predating this
// change show "<not set>" until Super Admin resets their password.
// ---------------------------------------------------------------------------
function PasswordVaultCard() {
  const { t } = useT();
  const [revealAll, setRevealAll] = React.useState(false);
  const [q, setQ] = React.useState("");
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin-user-passwords"],
    queryFn: async () => (await api.get("/admin/user-passwords")).data,
    staleTime: 30_000,
  });

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.email || "").toLowerCase().includes(needle) ||
      (r.phone || "").toLowerCase().includes(needle) ||
      (r.name || "").toLowerCase().includes(needle) ||
      (r.role || "").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const copyPw = async (r) => {
    if (!r.password_plain) return;
    try {
      await navigator.clipboard.writeText(r.password_plain);
      toast.success(`Copied ${r.email || r.phone}'s password`);
    } catch (e) {
      toast.error("Could not copy — your browser blocks clipboard access");
    }
  };

  const roleColor = (role) => {
    if (role === "super_admin") return "bg-purple-100 text-purple-800 border-purple-200";
    if (role === "back_office") return "bg-blue-100 text-blue-800 border-blue-200";
    if (role === "factory_1" || role === "factory_2") return "bg-orange-100 text-orange-800 border-orange-200";
    if (role === "packing_orders" || role === "inventory_packing") return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (role === "shipping_order") return "bg-teal-100 text-teal-800 border-teal-200";
    if (role === "three_pl") return "bg-stone-200 text-stone-800 border-stone-300";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  return (
    <section className="bg-white rounded-md border border-slate-200 p-5" data-testid="password-vault-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-semibold text-lg text-slate-800">{t("🔐 User Password Vault")}</h2>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search email, phone, name, role…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-64 h-8 text-xs"
            data-testid="vault-search"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRevealAll((v) => !v)}
            data-testid="vault-toggle-reveal"
          >
            {revealAll ? "🙈 Hide All" : "👁 Reveal All"}
          </Button>
        </div>
      </div>
      <div className="mt-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs leading-relaxed">
        <b>⚠️ Sensitive.</b> This page shows every account's current sign-in password in plain text.
        Only Super Admin can see this — the endpoint is role-gated. If a row shows
        <span className="font-mono mx-1">&lt;not set&gt;</span>, that user was created before Feb 28 2026 and
        their plaintext isn't stored yet. Reset the password from Users → Edit to reveal it.
      </div>
      <div className="mt-3 border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="vault-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[11px] uppercase tracking-widest font-mono text-slate-500">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email / Phone</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Password</th>
              <th className="px-3 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No users match "{q}"</td></tr>
            )}
            {!isLoading && filtered.map((r) => (
              <RowReveal key={r.id} r={r} revealAll={revealAll} roleColor={roleColor(r.role)} onCopy={() => copyPw(r)} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RowReveal({ r, revealAll, roleColor, onCopy }) {
  const qc = useQueryClient();
  const [local, setLocal] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [newPw, setNewPw] = React.useState("");
  const shown = revealAll || local || editing;
  const hasPw = !!r.password_plain;

  const changePw = useMutation({
    mutationFn: async (password) => (await api.patch(`/team/${r.id}`, { password })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user-passwords"] });
      toast.success(`Password updated for ${r.email || r.phone || r.name}`);
      setEditing(false); setNewPw("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update password"),
  });

  return (
    <tr className="hover:bg-slate-50 align-top" data-testid={`vault-row-${r.id}`}>
      <td className="px-3 py-2 font-medium text-slate-800">{r.name || <span className="text-slate-400">—</span>}</td>
      <td className="px-3 py-2 text-slate-700 font-mono text-xs">{r.email || r.phone || <span className="text-slate-400">—</span>}</td>
      <td className="px-3 py-2">
        <span className={`inline-block px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest font-mono ${roleColor}`}>
          {r.role}{r.sub_role ? ` · ${r.sub_role}` : ""}
        </span>
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <Input
              type="text"
              autoFocus
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newPw.trim().length >= 4 && !changePw.isPending) changePw.mutate(newPw.trim());
                if (e.key === "Escape") { setEditing(false); setNewPw(""); }
              }}
              placeholder="New password (min 4 chars)"
              className="h-7 text-xs font-mono"
              data-testid={`vault-pw-input-${r.id}`}
            />
            <Button
              size="sm"
              className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={newPw.trim().length < 4 || changePw.isPending}
              onClick={() => changePw.mutate(newPw.trim())}
              data-testid={`vault-pw-save-${r.id}`}
            >
              {changePw.isPending ? "…" : "Save"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => { setEditing(false); setNewPw(""); }}
            >
              ✕
            </Button>
          </div>
        ) : hasPw ? (
          shown ? (
            <span className="text-slate-900" data-testid={`vault-pw-${r.id}`}>{r.password_plain}</span>
          ) : (
            <span className="text-slate-400">••••••••</span>
          )
        ) : (
          <span className="text-slate-400 italic">&lt;not set&gt;</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          {hasPw && !editing && (
            <>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800 px-1"
                onClick={() => setLocal((v) => !v)}
                title={shown ? "Hide" : "Reveal"}
                data-testid={`vault-toggle-${r.id}`}
              >
                {shown ? "🙈" : "👁"}
              </button>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800 px-1"
                onClick={onCopy}
                title="Copy"
                data-testid={`vault-copy-${r.id}`}
              >
                📋
              </button>
            </>
          )}
          {!editing && (
            <button
              type="button"
              className="text-xs text-amber-700 hover:text-amber-900 px-1 font-semibold"
              onClick={() => { setEditing(true); setNewPw(""); }}
              title="Change password"
              data-testid={`vault-change-${r.id}`}
            >
              🔑
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Packing Checklist Template — Super Admin only. Reorder (↑↓), edit, add,
// deactivate the steps that every Packing / Inventory Packing wizard renders.
// Changes propagate to all unfinished orders immediately (the wizard re-fetches
// the template on every mount).
// ---------------------------------------------------------------------------
function PackingTemplateCard() {
  const { t } = useT();
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["packing-template"],
    queryFn: async () => (await api.get("/packing-checklist-template")).data,
    staleTime: 30_000,
  });

  const [addOpen, setAddOpen] = React.useState(false);
  const emptyAdd = { key: "", title: "", question: "", is_photo: false };
  const [addForm, setAddForm] = React.useState(emptyAdd);
  const [editKey, setEditKey] = React.useState(null);
  const [editForm, setEditForm] = React.useState(null);

  const addItem = useMutation({
    mutationFn: (payload) => api.post("/packing-checklist-template", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["packing-template"] });
      setAddOpen(false); setAddForm(emptyAdd);
      toast.success("Step added — appears on all unfinished orders");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add step"),
  });
  const updateItem = useMutation({
    mutationFn: ({ key, payload }) => api.patch(`/packing-checklist-template/${key}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["packing-template"] });
      setEditKey(null); setEditForm(null);
      toast.success("Step updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update step"),
  });
  const reorder = useMutation({
    mutationFn: (order) => api.post("/packing-checklist-template/reorder", { order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["packing-template"] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not reorder"),
  });
  const deactivate = useMutation({
    mutationFn: (key) => api.delete(`/packing-checklist-template/${key}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["packing-template"] });
      toast.success("Step deactivated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not deactivate"),
  });

  const move = (idx, delta) => {
    const next = idx + delta;
    if (next < 0 || next >= rows.length) return;
    const arr = rows.map((r) => r.key);
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    reorder.mutate(arr);
  };

  return (
    <section className="bg-white rounded-md border border-slate-200 p-5" data-testid="packing-template-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-semibold text-lg text-slate-800">{t("📋 Packing Checklist Template")}</h2>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="template-add-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          + Add step
        </Button>
      </div>
      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
        The list that every Packing / Inventory Packing wizard walks through. Reorder with the ↑↓ arrows,
        edit any row inline, or add a brand-new step. Changes reach every <b>unfinished</b> order immediately;
        already-completed steps on in-flight orders keep their existing marks.
      </p>
      <div className="mt-3 border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="template-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[11px] uppercase tracking-widest font-mono text-slate-500">
              <th className="px-2 py-2 w-14">Order</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Question</th>
              <th className="px-3 py-2 w-16">Photo?</th>
              <th className="px-3 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!isLoading && rows.map((r, i) => {
              const isEditing = editKey === r.key;
              return (
                <tr key={r.key} className="hover:bg-slate-50 align-top" data-testid={`template-row-${r.key}`}>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <div className="flex flex-col items-center gap-0.5">
                      <button
                        className="text-slate-500 hover:text-slate-800 disabled:opacity-30"
                        disabled={i === 0 || reorder.isPending}
                        onClick={() => move(i, -1)}
                        data-testid={`template-up-${r.key}`}
                        title="Move up"
                      >▲</button>
                      <span className="text-[10px] font-mono text-slate-400">{i + 1}</span>
                      <button
                        className="text-slate-500 hover:text-slate-800 disabled:opacity-30"
                        disabled={i === rows.length - 1 || reorder.isPending}
                        onClick={() => move(i, 1)}
                        data-testid={`template-down-${r.key}`}
                        title="Move down"
                      >▼</button>
                    </div>
                  </td>
                  {isEditing ? (
                    <>
                      <td className="px-3 py-2">
                        <Input
                          className="h-8"
                          value={editForm.title}
                          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          data-testid={`template-edit-title-${r.key}`}
                        />
                        <div className="text-[10px] font-mono text-slate-400 mt-1">key: {r.key}</div>
                      </td>
                      <td className="px-3 py-2">
                        <textarea
                          className="w-full h-16 rounded-md border border-slate-300 px-2 py-1 text-xs"
                          value={editForm.question}
                          onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                          data-testid={`template-edit-question-${r.key}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={editForm.is_photo}
                          onCheckedChange={(v) => setEditForm({ ...editForm, is_photo: !!v })}
                          data-testid={`template-edit-photo-${r.key}`}
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Button
                          size="sm"
                          className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white mr-1"
                          onClick={() => updateItem.mutate({ key: r.key, payload: { key: r.key, title: editForm.title, question: editForm.question, is_photo: editForm.is_photo, sort_order: r.sort_order, active: true } })}
                          disabled={updateItem.isPending}
                          data-testid={`template-save-${r.key}`}
                        >Save</Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7"
                          onClick={() => { setEditKey(null); setEditForm(null); }}
                        >Cancel</Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {r.title}
                        <div className="text-[10px] font-mono text-slate-400 mt-0.5">key: {r.key}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs leading-relaxed">{r.question || <span className="text-slate-400 italic">—</span>}</td>
                      <td className="px-3 py-2 text-center">
                        {r.is_photo ? <span className="text-emerald-700">📷</span> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          className="text-xs text-blue-700 hover:text-blue-900 mr-3"
                          onClick={() => { setEditKey(r.key); setEditForm({ title: r.title, question: r.question || "", is_photo: !!r.is_photo }); }}
                          data-testid={`template-edit-${r.key}`}
                        >✎ Edit</button>
                        {!r.system && (
                          <button
                            type="button"
                            className="text-xs text-red-700 hover:text-red-900"
                            onClick={() => {
                              if (!window.confirm(`Deactivate "${r.title}"? It stays in history but won't appear on new packing wizards.`)) return;
                              deactivate.mutate(r.key);
                            }}
                            data-testid={`template-delete-${r.key}`}
                          >🗑 Remove</button>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="template-add-dialog">
          <DialogHeader>
            <DialogTitle>➕ New checklist step</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono">Machine key <span className="text-red-600">*</span></Label>
              <Input
                data-testid="template-add-key"
                value={addForm.key}
                onChange={(e) => setAddForm({ ...addForm, key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
                placeholder="e.g. seatbelt_clip"
                className="font-mono"
              />
              <p className="text-[11px] text-slate-500 mt-1">Lowercase letters, digits and _ only. Cannot be changed later.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono">Title <span className="text-red-600">*</span></Label>
              <Input
                data-testid="template-add-title"
                value={addForm.title}
                onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                placeholder="e.g. Seatbelt Clip"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono">Question</Label>
              <textarea
                className="w-full h-20 rounded-md border border-slate-300 px-2 py-1 text-sm mt-1"
                value={addForm.question}
                onChange={(e) => setAddForm({ ...addForm, question: e.target.value })}
                placeholder="e.g. Is the seatbelt clip installed and locked?"
                data-testid="template-add-question"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={addForm.is_photo}
                onCheckedChange={(v) => setAddForm({ ...addForm, is_photo: !!v })}
                data-testid="template-add-photo"
              />
              <span>This step requires a photo upload to confirm</span>
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button
                onClick={() => addItem.mutate({ ...addForm, sort_order: (rows.length + 1) * 10, active: true, exp: "" })}
                disabled={!addForm.key.trim() || !addForm.title.trim() || addItem.isPending}
                data-testid="template-add-submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {addItem.isPending ? "Adding…" : "Add Step"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

